import { useMemo } from "react";
import LazyLoad from "react-lazyload";
import {
  Box,
  Grid,
  LinearProgress,
  Skeleton,
  TablePagination,
} from "@mui/material";

import { buildAggregateGroupRowKey } from "../../services/beefAiPricing";
import {
  InsightsEmptyState,
} from "../../components/Insights/InsightsAsyncState";
import { AGGREGATE_GRID_PAGE_SIZE_OPTIONS } from "./constants";
import PricingReportAggregateCard from "./PricingReportAggregateCard";

function PricingReportAggregateCardGrid({
  rows,
  coverUrlByRowKey,
  serverPagination,
  isLoading = false,
  isRefreshing,
  onCardClick,
}) {
  const coverLookup = coverUrlByRowKey instanceof Map ? coverUrlByRowKey : new Map();

  const cardCells = useMemo(
    () =>
      (rows || []).map((row, rowIndex) => {
        const rowKey = buildAggregateGroupRowKey(row.drilldown);
        const coverUrl = coverLookup.has(rowKey) ? coverLookup.get(rowKey) : null;
        return (
          <Grid key={`${rowKey}|${rowIndex}`} item xs={12} sm={6} md={4} lg={3} xl={3}>
            <Box sx={{ height: "100%" }}>
              <LazyLoad height="100%">
                <PricingReportAggregateCard
                  row={row}
                  coverUrl={coverUrl}
                  onCardClick={onCardClick}
                />
              </LazyLoad>
            </Box>
          </Grid>
        );
      }),
    [rows, coverLookup, onCardClick]
  );

  const showLoadingState = isLoading && !rows.length;
  const showEmptyState = !showLoadingState && !rows.length && serverPagination.totalCount === 0;

  if (showLoadingState) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 4 }, (_, index) => (
          <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
            <Skeleton variant="rounded" height={220} className="no-print" sx={{ width: "100%" }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (showEmptyState) {
    return (
      <InsightsEmptyState message="No rows for this report with the current filters." />
    );
  }

  return (
    <Box>
      {isRefreshing ? (
        <LinearProgress sx={{ mb: 1 }} aria-label="Loading aggregate results" />
      ) : null}
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          {cardCells}
        </Grid>
      </Box>
      <TablePagination
        component="div"
        count={serverPagination.totalCount}
        page={serverPagination.page}
        rowsPerPage={serverPagination.rowsPerPage}
        onPageChange={serverPagination.onPageChange}
        onRowsPerPageChange={serverPagination.onRowsPerPageChange}
        rowsPerPageOptions={AGGREGATE_GRID_PAGE_SIZE_OPTIONS}
      />
    </Box>
  );
}

export default PricingReportAggregateCardGrid;
